/**
 * Єдине джерело правди для всіх deep-link'ів у Telegram-бота.
 *
 * Тут НЕМАЄ дефолтних значень — і це свідомо. Платформа ставиться різним
 * клієнтам, і мовчазний фолбек на бота попереднього клієнта нічого не ламає
 * видимо: застосунок працює, просто веде людей у чужий бот. Тому кожне
 * посилання обов'язкове, а відсутність env валить модуль одразу й голосно.
 *
 * Налаштування — apps/web/.env (див. .env.example):
 *   VITE_TG_BOT_USERNAME — юзернейм бота без @
 *   VITE_TG_SUB_LINK     — підписка / продовження
 *   VITE_TG_CHAT_LINK    — чат простору
 *   VITE_TG_SUPPORT_LINK — підтримка
 *   VITE_TG_LOGIN_LINK   — CTA на сторінці логіну
 *
 * Кожне з чотирьох — повний https://t.me/... URL із start-параметром того
 * флоу, який його віддає в SendPulse.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} не задано. Юзернейм бота не має дефолту — заповни apps/web/.env за зразком .env.example.`,
    );
  }
  return value;
}

export const TG_BOT_USERNAME = required(
  'VITE_TG_BOT_USERNAME',
  import.meta.env.VITE_TG_BOT_USERNAME as string | undefined,
);

/**
 * Посилання на конкретний флоу. Якщо `start`-параметр ще не заведений, ведемо
 * у той самий бот без параметра — людина потрапляє в діалог і бачить меню.
 *
 * Тут фолбек безпечний, на відміну від видаленого: він виводиться з НАШОГО
 * юзернейма з env, а не з захардкодженого бота іншого клієнта. Найгірше, що
 * станеться — людина почне з головного меню замість потрібної гілки; вона не
 * потрапить у чужий простір.
 */
function flowLink(value: string | undefined): string {
  return value && value.trim() ? value : `https://t.me/${TG_BOT_USERNAME}`;
}

export const TG_SUB_LINK = flowLink(import.meta.env.VITE_TG_SUB_LINK as string | undefined);
export const TG_CHAT_LINK = flowLink(import.meta.env.VITE_TG_CHAT_LINK as string | undefined);
export const TG_SUPPORT_LINK = flowLink(import.meta.env.VITE_TG_SUPPORT_LINK as string | undefined);
export const TG_LOGIN_LINK = flowLink(import.meta.env.VITE_TG_LOGIN_LINK as string | undefined);

// ---------------------------------------------------------------------------
// Deep links — shareable links that open the Mini App straight on a specific
// material or section. Telegram passes the `startapp` value through to the web
// app as `initDataUnsafe.start_param`; AuthorizationPage reads it back and
// routes to /material/<id> or /section/<id> after the session is established.
//
// Format:  https://t.me/<bot>/?startapp=material_<uuid>
//          https://t.me/<bot>/?startapp=section_<uuid>
// This is the Direct Link form for the bot's *Main* Mini App (the one set via
// BotFather → Bot Settings → Configure Mini App). Telegram delivers the
// `startapp` value to the web app as `initDataUnsafe.start_param`.
//
// The trailing slash before `?` matters in practice: `t.me/<bot>/?startapp=`
// reliably opens the Main Mini App, whereas `t.me/<bot>?startapp=` (no slash)
// was observed to fall through to the bot chat. A `/app` path segment is a
// *named* Mini App (created with /newapp) and points at nothing here.
//
// `startapp` allows only [A-Za-z0-9_-]; a UUID fits (hyphens allowed, total
// length well under Telegram's 64-char cap).
//
// The bot username is passed in (from the session DTO) so the link targets the
// bot the Mini App is actually running under — dev bot on dev, prod bot on prod
// — rather than the hardcoded fallback.
// ---------------------------------------------------------------------------

const MATERIAL_START_PREFIX = 'material_';
const SECTION_START_PREFIX = 'section_';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function deepLink(prefix: string, id: string, botUsername?: string): string {
  const bot = botUsername || TG_BOT_USERNAME;
  return `https://t.me/${bot}/?startapp=${prefix}${id}`;
}

/** Build a shareable deep link that opens the Mini App on `materialId`.
 *  Pass the running bot's username (from the session) so the link points at the
 *  current environment's bot; falls back to VITE_TG_BOT_USERNAME when absent. */
export function materialDeepLink(materialId: string, botUsername?: string): string {
  return deepLink(MATERIAL_START_PREFIX, materialId, botUsername);
}

/** Build a shareable deep link that opens the Mini App on a section. */
export function sectionDeepLink(sectionId: string, botUsername?: string): string {
  return deepLink(SECTION_START_PREFIX, sectionId, botUsername);
}

/** Map a raw Telegram start param to the in-app route it should open, or null
 *  when it isn't a well-formed deep link. Inverse of the *DeepLink builders. */
export function routeFromStartParam(param: string | null | undefined): string | null {
  if (!param) return null;
  for (const [prefix, route] of [
    [MATERIAL_START_PREFIX, '/material/'],
    [SECTION_START_PREFIX, '/section/'],
  ] as const) {
    if (param.startsWith(prefix)) {
      const id = param.slice(prefix.length);
      return UUID_RE.test(id) ? `${route}${id}` : null;
    }
  }
  return null;
}
